import { useEffect } from 'react';

/**
 * Lightweight per-page SEO manager — no external dependency (keeps bundle size down).
 * Sets document.title, meta description, canonical link, robots meta, Open Graph /
 * Twitter tags, and an optional JSON-LD schema block for the current route.
 *
 * @param {Object} opts
 * @param {string} opts.title - Full <title> text.
 * @param {string} opts.description - Meta description (aim for ~150-160 chars).
 * @param {string} [opts.canonical] - Absolute canonical URL. Required for indexable pages.
 * @param {string} [opts.robots] - Robots directive, e.g. "noindex, follow". Defaults to "index, follow".
 * @param {string} [opts.image] - Absolute OG/Twitter image URL.
 * @param {Object|Array} [opts.schema] - JSON-LD object or array of objects to inject.
 * @param {string} [opts.type] - og:type, defaults to "website".
 */
export default function useSEO({
  title,
  description,
  canonical,
  robots = 'index, follow',
  image = 'https://www.primerehabindustries.in/Image/Logo.png',
  schema,
  type = 'website',
}) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (attr, key, content) => {
      if (content === undefined || content === null) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('name', 'description', description);
    setMeta('name', 'robots', robots);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:image', image);
    setMeta('property', 'og:site_name', 'Prime Rehab Industries');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', image);

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    let schemaScript = document.querySelector('#page-schema');
    if (schema) {
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.id = 'page-schema';
        schemaScript.type = 'application/ld+json';
        document.head.appendChild(schemaScript);
      }
      schemaScript.innerHTML = JSON.stringify(schema);
    } else if (schemaScript) {
      schemaScript.remove();
    }
    // Intentionally no cleanup: the next page's effect overwrites these tags,
    // which avoids a flash of empty <head> content during route transitions.
  }, [title, description, canonical, robots, image, schema, type]);
}
